import axios from "axios";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BASE_IMAGE_URL, BASE_URL } from "../../../config/apiConfig";
import { ToolProvider, useToolContext } from "../../../contexts/ToolContext";
import { getHeaders } from "../../../utils/apiUtils";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";

import { useTheme } from "@emotion/react";
import SelectableList from "../../../components/features/GarmentAttribute/SelectableList";
import ImageWindow from "../../../components/layout/Interactive/ImageWindow";
import KeyEventHandler from "../../../contexts/KeyEventHandler";
import MassImageApprovalsFrame from "../../../components/layout/Interactive/MassImageApprovalsFrame";
import { jobQaMap, jobTypes } from "../../../config/jobTypes";
import { showAlert, showAlertWithMessage } from "../../../components/common/userFeedback/CustomAlert";
import Slider from "@mui/material/Slider";
import TakeActionScreenCover from "../../../components/common/userFeedback/TakeActionScreenCover";
import LoadingOverlay from "../../../components/common/userFeedback/LoadingOverlay";
import NoDataScreenCover from "../../../components/common/userFeedback/NoDataScreenCover";
import CustomModal from "../../../components/common/userFeedback/CustomModal";
import SeedGarment from "../../../components/ui/cards/SeedGarment";
import { StyledButton } from "../../../components/ui/buttons/StyledButton";
import { getCurrentTimestamp } from "../../../utils/dateUtils";
import { useAmpltiudeEventTrackingContext } from "../../../contexts/AmplitudeTrackingContext";
import { APP_BAR_HEIGHT } from "../../../components/layout/AppBar/AppBarComponent";
import DatasetDropdown from "../../../components/forms/Dropdowns/DatasetDropdown";
import PineconeIndexDropdown from "../../../components/forms/Dropdowns/PineconeIndexDropdown";
import { formatToSpacedCapitalizedWords, logPerformancePoint } from "../../../utils/sharedFunctions";
import LoadingBar from "../../../components/ui/LoadingBar";
import { useAuth } from "../../../contexts/AuthContext";

function AggregationQATool() {
  const { isAdmin, isToolTester } = useAuth();
  const [garments, setGarments] = useState([]);
  const [selectedGarments, setSelectedGarments] = useState([]);
  const [pipelineJobIds, setPipelineJobIds] = useState([]);
  const [page, setPage] = useState(1);
  const [loadingLargestGroup, setLoadingLargestGroup] = useState(false);
  const [loadingGarments, setLoadingGarments] = useState(false);
  const [groupRepresentativeChanged, setGroupRepresentativeChanged] = useState(false);
  const {
    inputFilters,
    jobDetails,
    toolType,
    filterHardness,
    datasets,
    jobDataLoaded,
    allPipelineJobs,
    shownAttributes,
  } = useToolContext();
  const theme = useTheme();
  const { trackEvent } = useAmpltiudeEventTrackingContext();
  const [associatedGarmentsCount, setAssociatedGarmentsCount] = useState(0);
  const [rejectedGarmentsCount, setRejectedGarmentsCount] = useState(0);

  const [largestGroup, setLargestGroup] = useState({
    garment_count: 0,
    group_id: null,
  });
  const [groupRepresentative, setGroupRepresentative] = useState(null);

  const [summonTotal, setSummonTotal] = useState(0);
  const [numberOfColumns, setNumberOfColumns] = useState(4); // Default to 3 columns
  const [garmentLimit, setGarmentLimit] = useState(numberOfColumns * 3);
  const [totalGarmentsSeen, setTotalGarmentsSeen] = useState(0);
  const [garmentsSeen, setGarmentsSeen] = useState(0);
  const [selectedGroupRepresentative, setSelectedGroupRepresentative] =
    useState(null);
  const [groupRepresentativeModalOpen, setGroupRepresentativeModalOpen] =
    useState(false);
  const [deleteGroupModalOpen, setDeleteGroupModalOpen] =
    useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [groupID, setGroupID] = useState(0);
  const [presentedTimestamp, setPresentedTimestamp] = useState(getCurrentTimestamp())
  const [summonOnlyFromPipeline, setSummonOnlyFromPipeline] = useState(false);
  const [summonBinItems, setSummonBinItems] = useState(true);
  const [groupSize, setGroupSize] = useState(0);

  const [time, setTime] = useState(0);
  const timerLengthSeconds = (isToolTester && isAdmin) ? 0 : garmentLimit * 1.5;

  const [toolID, setToolID] = useState(0);
  const toolName = 'AggregationQATool'

  useEffect(() => {
    fetchToolData();
  }, [])

  useEffect(() => {
    setGarmentLimit(numberOfColumns * 3);
  }, [numberOfColumns]);

  useEffect(() => {
    setPageCount(largestGroup ? Math.ceil(largestGroup?.garment_count / garmentLimit) : 0);
  }, [garmentLimit, largestGroup])

  const handleNextPage = async () => {
    setLoadingGarments(true);
    setPage(page+1);
  }

  const fetchToolData = async () => {
    const headers = await getHeaders();
    const formattedToolName = formatToSpacedCapitalizedWords(toolName);
    let url = `${BASE_URL}tools/jobs/tool-names?toolName=${toolName}`;
    axios
      .get(url, headers)
      .then((res) => {
        console.log(`tool info: ${JSON.stringify(res.data.data)}`);
        if (res.data.data && res.data.data.length > 0 && res.data.data[0].id && res.data.data[0].id > 0)
        {
          setToolID(res.data.data[0].id)
        } else {
          const err = `Could not find tool ID in 'tool_names' with name '${formattedToolName}' (formatted from '${toolName}').`
          console.log(err);
          showAlert(err);
        }
      })

      .catch((err) => {
        console.log(err);
        showAlert(err);
      });
  };

  useEffect(() => {
    if (jobDataLoaded && !loadingGarments && !loadingLargestGroup)
    {
      const start = Date.now();
      const interval = setInterval(() => {
        const elapsed = (Date.now() - start) / 1000; // seconds
        setTime(Math.min(elapsed, timerLengthSeconds)); // clamp to 30
      }, 100);
    
      return () => clearInterval(interval);
    }
  }, [jobDataLoaded, loadingGarments, loadingLargestGroup, page])

  useEffect(() => {
    if (jobDataLoaded && ((!garments || garments.length === 0) && (page === 1 && (pageCount === 0)))) {
      fetchSummonCount();
      fetchSeedGarment();
    }
  }, [jobDataLoaded, garments, page, pageCount, summonOnlyFromPipeline, summonBinItems]);
  
  async function fetchSeedGarment() {
    setGroupRepresentative(null);
    setGarmentsSeen(0);
    setPage(1);
    const headers = await getHeaders();
    setLoadingLargestGroup(true);
    const jobsToQa = jobQaMap[toolType];
    const jobIdsToQa = allPipelineJobs
      .filter((job) => jobsToQa.includes(job.job))
      .map((job) => job.pipeline_job_link_id);
    setPipelineJobIds(jobIdsToQa)

    const submissionData = {
      toolType: toolType,
      pipelineJobIdsToQa: jobIdsToQa,
      pipelineJobId: jobDetails.pipeline_job_link_id,
      jobID: jobDetails.job_id,
      datasetIds: datasets,
      vectorNamespaceSize: summonTotal ?? 0,
      offsetLimit: 10,
      summonOnlyFromPipeline: summonOnlyFromPipeline,
      summonBinItems: summonBinItems
    };
    try {
      const res = await axios.post(
        `${BASE_URL}tools/summon/next-item/qa/aggregation`,
        submissionData,
        headers
      );

      const tempGarmentImages = Array.from({ length: res.data.data.representative_garment.num_images }, (_, i) => {
        return `https://scraped-garment-images.s3.eu-west-2.amazonaws.com/${
          res.data.data.representative_garment.uuid_public_id
        }-${i + 1}.jpg`;
      });

      setGroupRepresentative({
        ...res.data.data.representative_garment,
        images: tempGarmentImages,
        proposed_timestamp: getCurrentTimestamp(),
      });
      setLargestGroup(res.data.data.group);
      setPageCount(Math.ceil(res.data.data.group?.garment_count / garmentLimit));
      setGroupID(res.data.data.group.group_id);
      setSelectedGroupRepresentative(null);
      setLoadingLargestGroup(false);
      fetchGarments(res.data.data.group.group_id);
      setGroupSize(res.data.data.group?.garment_count ?? 0)
    } catch (err) {
      let error = err
      if (summonTotal === 0)
      {
        error.message = "No data found."
        showAlertWithMessage(error.message, "error");
      } else {
        showAlert(error);
      }
      console.log(error);
      setLoadingLargestGroup(false);
    }
  }

  async function fetchGarments(groupIDProvided=null) {
    const headers = await getHeaders();
    if (!groupID && !groupIDProvided) {
      return;
    }
    if (loadingGarments) {
      return;
    }
    setLoadingGarments(true);

    try {
      const res = await axios.post(
        `${BASE_URL}tools/summon/list-associations/qa/aggregation`,
        {
          group_id: groupIDProvided ?? groupID,
          limit: garmentLimit,
        },
        headers
      );
      const data = res.data.data.map(x => {return { ...x, presented_timestamp: presentedTimestamp }})
      setGarments(data);
      setPresentedTimestamp(getCurrentTimestamp());
      if ((!groupIDProvided && data.length === 0) || (data.length === 1 && data.filter(x => x.garment_id === groupRepresentative.garment_id).length === 1)) {
        // CONSOLIDATE group rep if no members found
        const resConsolidate = await axios.post(
          `${BASE_URL}tools/classify/qa/aggregation/consolidate-representative`,
          {
            newGroupRepresentativeGarmentId: groupRepresentative.garment_id,
            groupID: groupID,
            confidence: 0.4,
            garmentsSeen: garmentsSeen,
            totalGarmentsSeen: totalGarmentsSeen,
            jobId: jobDetails.job_id,
            pipelineJobId: jobDetails.pipeline_job_link_id,
            toolId: toolID,
            proposedTimestamp: Date.now(),
            presentedTimestamp: presentedTimestamp ?? Date.now()
          },
          headers
        );
        const dataConsolidate = res.data.data
    
        trackEvent("AggregationQANextPage", {
          datasetId: datasets,
          garmentId: groupRepresentative["garment_id"],
          aggregations: associatedGarmentsCount,
          totalAggregations: associatedGarmentsCount,
          selectedGarmentIds: [],
          rejectedGarmentIds: [],
          garmentsSelected: 0,
          garmentsRejected: 0,
          garmentsSeen: garmentsSeen,
          totalGarmentsSeen: totalGarmentsSeen,
          page: page,
          pageCount: pageCount,
          jobId: jobDetails.job_id,
          pipelineJobId: jobDetails.pipeline_job_link_id,
          pipelineJobIdsToQA: pipelineJobIds,
          pipelineName: jobDetails.pipeline_name,
          jobName: "aggregationQA",
          toolType: "AGGREGATION_QA",
          toolName: "AggregationQATool",
          toolId: toolID,
          usedVectorNamespaceId: garments[0].used_vector_namespace_id ?? 0,
          qa: true,
          skipped: selectedGarments.length === 0, //"Skipped" is no selected garments
        });

        setSelectedGarments([]);
        setLargestGroup(null);
      }
      setTotalGarmentsSeen(totalGarmentsSeen + (data.length));
      setGarmentsSeen(garmentsSeen + (data.length));
      setLoadingGarments(false);
    } catch (error) {
      showAlert(error);
      setLoadingGarments(false);
    }
  }
  
  const fetchSummonCount = async () => {
    const headers = await getHeaders();
    
    const jobsToQa = jobQaMap[toolType];
    const jobIdsToQa = allPipelineJobs
      .filter((job) => jobsToQa.includes(job.job))
      .map((job) => job.pipeline_job_link_id);

    const params = {
      toolType: toolType,
      pipelineJobIdsToQa: jobIdsToQa,
      pipelineJobId: jobDetails.pipeline_job_link_id,
      jobID: jobDetails.job_id,
      datasetIds: datasets,
      summonOnlyFromPipeline: summonOnlyFromPipeline,
      summonBinItems: summonBinItems
    };
    
    setLoadingGarments(true);
    try {
      const headers = await getHeaders();
      const res = await axios.post(
        `${BASE_URL}tools/summon/count/aggregation/qa`,
        params,
        headers
      )

      const t0 = logPerformancePoint(0);
      setSummonTotal(res.data.data.total_count);
      if (res.data.data.total_count === 0)
      {
        setGroupRepresentative(null);
      }
      console.log(`res.data.data.total_count: ${res.data.data.total_count}`)
      const t1 = logPerformancePoint(1, t0);
      
      setLoadingGarments(false);
    } catch (err) {
      showAlert(err);
      setLoadingGarments(false);
    };
  };
              
  const handleControlClick = (garmentId) => {
    setSelectedGroupRepresentative(garmentId)
    setGroupRepresentativeModalOpen(true);
  };

  const handleNextPageSubmit = async () => {
    const groupShouldBeDeleted = selectedGarments.find(tag => tag.garment_id === groupRepresentative.garment_id)
    if (groupShouldBeDeleted)
    {
      setDeleteGroupModalOpen(true);
    } else {
      handleSubmit();
    }
  }

  const handleSubmit = async () => {
    const t0 = logPerformancePoint(0);
    setLoadingGarments(true);
    // Instead of merging selectedGarments into the approved set, we now treat them as truly rejected.
    // The 'combinedArray' will now represent the garments we are approving, i.e., all garments
    // that are NOT in selectedGarments.

    // Create a set of rejected garment IDs for easy lookup
    const rejectedIds = new Set(selectedGarments.map((tag) => tag.garment_id));

    // The combinedArray now will be all garments that are NOT in selectedGarments.
    // These garments represent the approved set.
    let combinedArray = garments
      .filter((g) => !rejectedIds.has(g.garment_id))
      .map((g) => ({
        ...g,
        presented_timestamp: presentedTimestamp,
      }));

    // If the representative garment changed, also include it in the approvals.
    // (Same logic as before, but now this goes into the approved set, not the rejected set.)
    if (groupRepresentativeChanged) {
      combinedArray = combinedArray.map((garment) => selectedGroupRepresentative === garment.garment_id 
        ? { is_group_representative: true, ...garment } 
        : { is_group_representative: false, ...garment })
    }

    // The approved garments are now those in 'combinedArray'
    const approved = combinedArray.map((g) => g.garment_id);

    // The rejected garments are now those explicitly listed in selectedGarments
    const rejected = selectedGarments.map((tag) => tag.garment_id);

    // Update counts
    setAssociatedGarmentsCount((prev) => prev + combinedArray.length);
    setRejectedGarmentsCount((prev) => prev + selectedGarments.length);
    const params = {
      newGroupRepresentativeGarmentId: groupRepresentative.garment_id,
      approvedGarments: approved,
      rejectedGarments: rejected,
      groupID: groupID,
      confidence: 0.4,
      garmentsSeen: garmentsSeen,
      totalGarmentsSeen: totalGarmentsSeen,
      jobId: jobDetails.job_id,
      pipelineJobId: jobDetails.pipeline_job_link_id,
      toolId: toolID,
      proposedTimestamp: Date.now(),
      presentedTimestamp: presentedTimestamp ?? Date.now()
    };

    try {
      const headers = await getHeaders();
      const res = await axios.post(
        `${BASE_URL}tools/classify/qa/aggregation`,
        params,
        headers
      );

      if (groupRepresentativeChanged)
      {
        setGroupID(res.data.data.new_group_id);
      }

      trackEvent("AggregationQA", {
        datasetId: datasets,
        garmentId: groupRepresentative["garment_id"],
        aggregations: associatedGarmentsCount,
        totalAggregations: combinedArray.length,
        selectedGarmentIds: Object.keys(approved),
        rejectedGarmentIds: Object.keys(rejected),
        garmentsSelected: Object.keys(approved).length,
        garmentsRejected: Object.keys(rejected).length,
        garmentsSeen: garmentsSeen,
        totalGarmentsSeen: totalGarmentsSeen,
        jobId: jobDetails.job_id,
        pipelineJobId: jobDetails.pipeline_job_link_id,
        pipelineJobIdsToQA: pipelineJobIds,
        pipelineName: jobDetails.pipeline_name,
        jobName: "aggregationQA",
        toolType: "AGGREGATION_QA",
        toolName: "AggregationQATool",
        toolId: toolID,
        usedVectorNamespaceId: garments[0].used_vector_namespace_id ?? 0,
        qa: true,
        skipped: combinedArray.length === 0, //"Skipped" is no selected garments
      });
      
      // Reset states after successful submission
      setSelectedGarments([]);
      setGroupRepresentativeChanged(false);
      const t1 = logPerformancePoint(1,t0);
      setGroupSize(groupSize - garments.length)
      fetchGarments(res.data.data.new_group_id);
      const t2 = logPerformancePoint(2,t1);
    } catch (err) {
      console.error(err)
      showAlert(err);
      setLoadingGarments(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${APP_BAR_HEIGHT}px)`,
        p: 2,
        pt: 0,
        pb: 1
      }}
    >
      <LoadingOverlay
        open={loadingGarments || loadingLargestGroup}
        text={
          loadingGarments
            ? "Loading Garments"
            : loadingLargestGroup
            ? "Loading Seed Garment"
            : ""
        }
      />
            
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          mt: 1,
        }}
      >
        <Typography variant="h2" sx={{ 
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          width: "100%"
        }}>
          Aggregation QA Tool
        </Typography>
          
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            mt: 2,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            sx={{ 
              color: theme.palette.primary[100],
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              width: "100%" 
            }}
            disabled={
              loadingGarments || loadingLargestGroup || !groupRepresentative || (page === pageCount) || time < timerLengthSeconds
            }
            onClick={() => {
              handleNextPage();
              handleNextPageSubmit()
            }}
          >
            Next Page
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{
              marginLeft: 2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              width: "100%",
              px: 3
            }}
            onClick={() => {
              handleNextPageSubmit();
              setPage(1);
              setPageCount(0);
            }}
            disabled={
              loadingGarments || loadingLargestGroup || !groupRepresentative || (page !== pageCount) || time < timerLengthSeconds
            }
          >
            Next Garment
          </Button>
        </Box>
      </Box>

      <Box sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-end",
        mr: 2,
        width: "100%",
        mt: 1
      }}>
        <LoadingBar time={time} maxTime={timerLengthSeconds} sx={{maxWidth: "19rem"}}/>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          height: 60,
          alignContent: "center",
          mt: -1
        }}
      >
        <Select
          value={numberOfColumns}
          onChange={(e) => setNumberOfColumns(e.target.value)}
          displayEmpty
        >
          <MenuItem value={4}>4 Columns</MenuItem>
          <MenuItem value={5}>5 Columns</MenuItem>
          <MenuItem value={6}>6 Columns</MenuItem>
          <MenuItem value={7}>7 Columns</MenuItem>
          <MenuItem value={8}>8 Columns</MenuItem>
        </Select>

        <Button
          variant="contained-white"
          color="primary"
          sx={[{
            //overflow: "hidden",
            //whiteSpace: "nowrap"
            py: 1,
            mb: 1,
            ml: 2
            },
            summonOnlyFromPipeline && {
                backgroundColor: `${theme.palette.primary[300]} !important`,
                color: `${theme.palette.primary.main} !important`
            }]}
          onClick={() => setSummonOnlyFromPipeline(!summonOnlyFromPipeline)}
        >
          {summonOnlyFromPipeline ? `ONLY IN PIPELINE` : `ALL IN DATASET`}
        </Button>

        <Button
          variant="contained-white"
          color="primary"
          sx={[{
            //overflow: "hidden",
            //whiteSpace: "nowrap"
            py: 1,
            mb: 1,
            ml: 2
            },
            summonBinItems && {
                backgroundColor: `${theme.palette.primary[300]} !important`,
                color: `${theme.palette.primary.main} !important`
            }]}
          onClick={() => setSummonBinItems(!summonBinItems)}
        >
          {summonBinItems ? `INCLUDE` : `EXCLUDE`} BIN
        </Button>
      </Box>

      <Divider
        sx={{
          m: 1,
        }}
      />
      
      <Box
        sx={{
          flexGrow: 1, // Fills the remaining space
          display: "flex",
          overflow: "hidden", // Ensures no overflow outside this box
        }}
      >
      {/*disabledClickFunction={(garment_id) => garment_id === groupRepresentative.garment_id}*/}
      {((!summonTotal || summonTotal === 0))
        ? (
          <NoDataScreenCover sx={{marginLeft: "25%", marginRight: "75%", minWidth: "50vw"}}/>
        )
        : ((garments.length !== 0) && !loadingGarments && !loadingLargestGroup ? (
            <Grid container sx={{height: "90vh"}}>
              <Grid
                xs={3}
              >
                <Typography variant="h3">Seed Garment</Typography> {/* Group Representative */}

                <SeedGarment garment={groupRepresentative}  sx={{py: 1, height: "75%"}} sxImageCarousel={{height: "80%"}}/>
              </Grid>

              <Grid xs={9} sx={{ml: 2, mr: -2}}>
                <Typography variant="h3" sx={{mb: 1}}>Garment Pool</Typography>
                <MassImageApprovalsFrame
                  sx={{p: 2, pt: 0, pl: 0}}
                  garments={garments}
                  selectedGarments={selectedGarments}
                  setSelectedGarments={setSelectedGarments}
                  handleControlClick={handleControlClick}
                  disabled={false}
                  reject={true}
                  numberOfColumns={numberOfColumns}
                  presentedTimestamp={presentedTimestamp}
                  groupRepresentativeBorder={true}
                />
              </Grid>
            </Grid>
          ) : (
            <TakeActionScreenCover />
          ))}
  </Box>
  
  <Divider
        sx={{
          m: 1,
        }}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="body_sm">
          Page: {page} /{" "}
          {largestGroup?.garment_count <= 0
            ? 0
            : pageCount}
        </Typography>
        <Typography variant="body_sm">
          Total Matched: <Typography variant="body_sm" color="success.main">{associatedGarmentsCount}</Typography>
        </Typography>
        <Typography variant="body_sm">
          Total Rejected: <Typography variant="body_sm" color="error.main">{rejectedGarmentsCount}</Typography>
        </Typography>
        <Typography variant="body_sm">Group Size: {groupSize}</Typography>
        <Typography variant="body_sm" sx={{userSelect: "text"}}>Group ID: {groupID}</Typography>
      </Box>
      
      <CustomModal
        showModal={groupRepresentativeModalOpen}
        title={"Change Representative"} //Group Representative
        body={
          "Are you sure you would like to change the representative?"
        }
        onDismissTitle="Return"
        onConfirmTitle="Confirm"
        onConfirm={() => {
          // Find Garment
          const tempGarment = garments.find((g) => g.garment_id === selectedGroupRepresentative);
          const tempGarmentImages = Array.from({ length: tempGarment.num_images }, (_, i) => {
            return `https://scraped-garment-images.s3.eu-west-2.amazonaws.com/${
              tempGarment.uuid_public_id
            }-${i + 1}.jpg`;
          });
          const oldRepresentative = groupRepresentative;
          setGroupRepresentative({
            ...tempGarment,
            images: tempGarmentImages,
            proposed_timestamp: getCurrentTimestamp(),
          });
          setSelectedGarments(selectedGarments.filter(garment => garment.garment_id !== selectedGroupRepresentative))

          trackEvent("AggregationQAUpdateRepresentative", {
            index: garments && garments.length > 0 ? garments[0].used_vector_namespace_id : 0,
            datasetId: datasets,
            garmentId: groupRepresentative["garment_id"],
            garmentsSeen: garmentsSeen,
            totalGarmentsSeen: totalGarmentsSeen,
            proposedTimestamp:
              groupRepresentative["proposed_timestamp"],
            jobId: jobDetails.job_id,
            pipelineJobId: jobDetails.pipeline_job_link_id,
            pipelineJobIdsToQA: pipelineJobIds,
            pipelineName: jobDetails.pipeline_name,
            jobName: "aggregationQA",
            toolType: "AGGREGATION_QA",
            toolName: "AggregationQATool",
            toolId: toolID,
            usedVectorNamespaceId: garments[0].used_vector_namespace_id ?? 0,
            qa: true,
          });
          
          trackEvent("UpdateRepresentative", {
            index: garments && garments.length > 0 ? garments[0].used_vector_namespace_id : 0,
            datasetId: datasets,
            garmentId: groupRepresentative["garment_id"],
            garmentsSeen: garmentsSeen,
            totalGarmentsSeen: totalGarmentsSeen,
            jobId: jobDetails.job_id,
            pipelineJobId: jobDetails.pipeline_job_link_id,
            pipelineName: jobDetails.pipeline_name,
            oldRepresentativeGarmentId: oldRepresentative.garment_id,
            newRepresentativeGarmentId: tempGarment.garment_id,
            jobName: "aggregationQA",
            toolType: "AGGREGATION_QA",
            toolName: "AggregationQATool",
            toolId: toolID,
            usedVectorNamespaceId: garments[0].used_vector_namespace_id ?? 0,
            qa: true,
          });

          setGroupRepresentativeModalOpen(false);
          setSelectedGroupRepresentative(null);
          setGroupRepresentativeChanged(true);
        }}
        onDismiss={() => {
          setSelectedGroupRepresentative(null);
          setGroupRepresentativeModalOpen(false);
        }}
      />
      <CustomModal
        showModal={deleteGroupModalOpen}
        title={"Confirm Group Deletion"}
        body={
          <>Are you sure you want to decline the representative?<br /><b>Please choose a different representative.<br/>If there is no other representative garment avaiable, this will delete the group</b>.</> /* or if all proposals are declined */
        }
        onDismissTitle="No"
        onConfirmColour="error"
        onConfirmTitle="Yes"
        onConfirm={handleSubmit}
        onDismiss={() => {
          setSelectedGarments(selectedGarments.filter(garment => garment.garment_id !== groupRepresentative.garment_id))
          setDeleteGroupModalOpen(false)
        }}
      />
    </Box>
  );
}
function WrappedAggregationQATool() {
  const { id } = useParams();
  return (
    <ToolProvider id={id}>
      <AggregationQATool />
    </ToolProvider>
  );
}

export default WrappedAggregationQATool;
